import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Save, Lock, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const OTHER_REASON = "Other (specify)";

export default function TaskEditDialog({ task, onClose, onSaved }) {
  const { canEditDept } = useAuth();
  const editable = canEditDept(task.dept);
  const isProc = !!task.is_procurement;

  const [procType, setProcType] = useState(task.procurement_type || "material_po");
  const [status, setStatus] = useState(task.status);
  const [actual, setActual] = useState(task.actual_hours ?? 0);
  const [due, setDue] = useState(task.due_date || "");
  const [overdueReason, setOverdueReason] = useState(task.overdue_reason || "");
  const [overdueNote, setOverdueNote] = useState(task.overdue_reason_note || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Status + reason lists switch with procurement type.
  const statusOptions = useMemo(() => {
    if (isProc && task.proc_status_options) return task.proc_status_options[procType] || [];
    return task.status_options || [];
  }, [isProc, procType, task]);

  const reasonOptions = useMemo(() => {
    if (isProc && task.proc_reason_options) return task.proc_reason_options[procType] || [];
    return task.overdue_reason_options || [];
  }, [isProc, procType, task]);

  const changeProcType = (t) => {
    setProcType(t);
    const opts = task.proc_status_options?.[t] || [];
    if (!opts.includes(status)) setStatus(opts[0]);
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { status, actual_hours: Number(actual), due_date: due };
      if (isProc) payload.procurement_type = procType;
      if (task.overdue) {
        payload.overdue_reason = overdueReason || null;
        payload.overdue_reason_note = overdueReason === OTHER_REASON ? overdueNote : null;
      }
      const { data } = await api.put(`/tasks/${task.id}`, payload);
      onSaved?.(data);
      onClose();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="task-edit-dialog"
          className="w-full max-w-lg card-surface p-6 max-h-[92vh] overflow-y-auto"
        >
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-xs text-gold/80 uppercase tracking-wider">{task.project_job_code}</div>
              <h3 className="font-heading text-xl mt-0.5">{task.stage_label}</h3>
              <div className="text-sm text-muted-foreground">{task.project_name}</div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid="dialog-close">
              <X size={18} />
            </button>
          </div>

          {!editable ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 border border-border rounded-md px-3 py-3">
              <Lock size={15} className="text-gold" />
              You can't edit <span className="text-foreground font-medium">{task.dept}</span> tasks with your role. View only.
            </div>
          ) : (
            <div className="mt-5 space-y-4">
              {isProc && (
                <div>
                  <span className="text-xs text-muted-foreground mb-2 block">Procurement type</span>
                  <div className="grid grid-cols-2 gap-2">
                    {["material_po", "sub_contract"].map((t) => (
                      <button
                        key={t}
                        data-testid={`proc-type-${t}`}
                        onClick={() => changeProcType(t)}
                        className={`px-3 py-2 rounded-md text-sm border transition-all ${
                          procType === t ? "border-gold/50 bg-gold/12 text-gold" : "border-border hover:bg-muted text-muted-foreground"
                        }`}
                      >
                        {task.procurement_type_labels?.[t] || (t === "material_po" ? "Material PO" : "Sub-Contract")}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <span className="text-xs text-muted-foreground mb-1.5 block">Status</span>
                <select
                  data-testid="status-select" value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50"
                >
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {status === statusOptions[statusOptions.length - 1] && (
                  <div className="text-[11px] text-success mt-1.5">Marks this stage complete.</div>
                )}
              </div>

              {task.overdue && (
                <div className="rounded-md border border-critical/30 bg-critical/5 p-3 space-y-2.5" data-testid="overdue-reason-block">
                  <div className="flex items-center gap-2 text-sm text-critical">
                    <AlertTriangle size={15} /> {Math.abs(task.days_left)} day(s) overdue — reason
                  </div>
                  <select
                    data-testid="overdue-reason-select" value={overdueReason}
                    onChange={(e) => setOverdueReason(e.target.value)}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-critical/50"
                  >
                    <option value="">Select a reason…</option>
                    {reasonOptions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  {overdueReason === OTHER_REASON && (
                    <input
                      data-testid="overdue-reason-note" type="text" value={overdueNote}
                      placeholder="Specify the reason…"
                      onChange={(e) => setOverdueNote(e.target.value)}
                      className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-critical/50"
                    />
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-muted-foreground mb-1.5 block">Actual hours</span>
                  <input
                    data-testid="input-actual-hours" type="number" min="0" value={actual}
                    onChange={(e) => setActual(e.target.value)}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-muted-foreground mb-1.5 block">Due date (override)</span>
                  <input
                    data-testid="input-due-date" type="date" value={due || ""}
                    onChange={(e) => setDue(e.target.value)}
                    className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-gold/50"
                  />
                </label>
              </div>

              <div className="text-xs text-muted-foreground">
                Planned hours: <span className="font-mono-num text-foreground">{task.planned_hours}</span>
              </div>

              {err && <div className="text-sm text-critical bg-critical/10 border border-critical/30 rounded-md px-3 py-2">{err}</div>}

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-border hover:bg-muted">Cancel</button>
                <button
                  onClick={save} disabled={busy}
                  data-testid="task-save-btn"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-gold text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-60"
                >
                  <Save size={15} /> {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
